import { c as _c } from "react/compiler-runtime";
import { Progress } from '@ark-ui/react/progress';
import { css, cva, cx } from '@repo/panda/css';
import type React from 'react';
import type { ThemeColorValue } from '../utils/theme-utils';
const progressBarRootStyles = cva({
  base: {
    width: 'full'
  },
  variants: {
    fullWidth: {
      true: {
        width: 'full'
      },
      false: {
        width: 'fit'
      }
    }
  },
  defaultVariants: {
    fullWidth: true
  }
});
const progressBarTrackStyles = cva({
  base: {
    width: 'full',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 'pill'
  },
  variants: {
    size: {
      xs: {
        height: '[4px]'
      },
      sm: {
        height: '[6px]'
      },
      md: {
        height: '[8px]'
      },
      lg: {
        height: '[12px]'
      },
      xl: {
        height: '[16px]'
      },
      '2xl': {
        height: '[24px]'
      }
    },
    variant: {
      primary: {
        backgroundColor: 'fill.default'
      },
      secondary: {
        backgroundColor: 'fill.default'
      },
      success: {
        backgroundColor: 'status.success'
      },
      warning: {
        backgroundColor: 'yellow.500'
      },
      error: {
        backgroundColor: 'status.error'
      },
      info: {
        backgroundColor: 'status.info'
      },
      gamify: {
        backgroundColor: 'green.900'
      },
      accent: {
        backgroundColor: 'fill.stronger'
      }
    }
  },
  defaultVariants: {
    size: 'md',
    variant: 'primary'
  }
});
const progressBarFillStyles = cva({
  base: {
    height: 'full',
    transitionProperty: 'size',
    transitionDuration: 'base',
    transitionTimingFunction: 'easeOut'
  },
  variants: {
    variant: {
      primary: {
        backgroundColor: 'neutral95'
      },
      secondary: {
        backgroundColor: 'neutral05'
      },
      success: {
        backgroundColor: 'status.success'
      },
      warning: {
        backgroundColor: 'yellow.500'
      },
      error: {
        backgroundColor: 'status.error'
      },
      info: {
        backgroundColor: 'status.info'
      },
      gamify: {
        backgroundColor: 'green.500'
      },
      accent: {
        backgroundColor: 'accent'
      }
    },
    indeterminate: {
      true: {
        position: 'absolute',
        width: '1/2',
        animationName: 'pulse',
        animationDuration: '[1.5s]',
        animationIterationCount: 'infinite'
      },
      false: {}
    }
  },
  defaultVariants: {
    variant: 'primary',
    indeterminate: false
  }
});
export type ProgressBarVariants = NonNullable<Parameters<typeof progressBarFillStyles>[0]>;
export interface ProgressBarProps {
  value?: number;
  max?: number;
  size?: NonNullable<Parameters<typeof progressBarTrackStyles>[0]>['size'];
  variant?: ProgressBarVariants['variant'];
  indeterminate?: boolean;
  className?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  color?: ThemeColorValue;
  backgroundColor?: ThemeColorValue;
}
export const ProgressBar: React.FC<ProgressBarProps> = t0 => {
  const $ = _c(21);
  const {
    value: t1,
    max,
    size,
    variant,
    indeterminate,
    className,
    style,
    fullWidth,
    color,
    backgroundColor
  } = t0;
  const value = t1 === undefined ? 0 : t1;
  const percentage = max ? value / max * 100 : value;
  const clampedValue = Math.min(Math.max(percentage, 0), 100);
  let t2;
  if ($[0] !== className || $[1] !== fullWidth) {
    t2 = cx(progressBarRootStyles({
      fullWidth
    }), className);
    $[0] = className;
    $[1] = fullWidth;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== backgroundColor || $[4] !== size || $[5] !== variant) {
    t3 = cx(progressBarTrackStyles({
      size,
      variant
    }), css({
      backgroundColor
    }));
    $[3] = backgroundColor;
    $[4] = size;
    $[5] = variant;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== color || $[8] !== indeterminate || $[9] !== variant) {
    t4 = cx(progressBarFillStyles({
      variant,
      indeterminate
    }), css({
      backgroundColor: color
    }));
    $[7] = color;
    $[8] = indeterminate;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <Progress.Range className={t4} />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <Progress.Track className={t3}>{t5}</Progress.Track>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== clampedValue || $[17] !== style || $[18] !== t2 || $[19] !== t6) {
    t7 = <Progress.Root value={clampedValue} className={t2} style={style}>{t6}</Progress.Root>;
    $[16] = clampedValue;
    $[17] = style;
    $[18] = t2;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};